/**
 * SOVRANOVA — voice session resilience
 *
 * Two problems this solves, shared by Live and Vent:
 *
 *  1. The screen sleeps mid-session. Mobile browsers suspend the AudioContext
 *     and drop the WebSocket; nothing on our side noticed, so the user came
 *     back to a UI that believed it was connected and was not — frozen.
 *  2. Nothing kept the screen awake in the first place, so a quiet moment in a
 *     therapeutic session (exactly when the user is *meant* to be still) put
 *     the phone to sleep and killed the session.
 *
 * Design note: browsers will not let audio resume without a user gesture in
 * every case, so we ATTEMPT a silent reconnect on return and fall back to an
 * explicit "tap to resume" rather than pretending. A visible one-tap resume is
 * honest; a frozen screen is not.
 */
import { useEffect, useRef, useState } from 'react'

/** Hold a screen wake lock while `enabled`, re-acquiring after tab switches. */
export function useWakeLock(enabled) {
  const lockRef = useRef(null)

  useEffect(() => {
    if (!enabled || typeof navigator === 'undefined' || !('wakeLock' in navigator)) return
    let cancelled = false

    const acquire = async () => {
      try {
        if (lockRef.current || document.visibilityState !== 'visible') return
        const lock = await navigator.wakeLock.request('screen')
        if (cancelled) { lock.release().catch(() => {}); return }
        lockRef.current = lock
        lock.addEventListener('release', () => { lockRef.current = null })
      } catch { /* wake lock is best-effort — never break the session over it */ }
    }

    // The lock is dropped automatically whenever the page is hidden, so it has
    // to be taken again every time we come back.
    const onVisible = () => { if (document.visibilityState === 'visible') acquire() }

    acquire()
    document.addEventListener('visibilitychange', onVisible)
    return () => {
      cancelled = true
      document.removeEventListener('visibilitychange', onVisible)
      lockRef.current?.release?.().catch(() => {})
      lockRef.current = null
    }
  }, [enabled])
}

/**
 * Detect a session that died while backgrounded and try to bring it back.
 *
 * @param active     whether we believe a session is running
 * @param isOpen     () => boolean — true when the transport is genuinely open
 * @param reconnect  async () => void — re-establish the session
 * @returns { suspended, resuming, resume } — render a resume affordance when
 *          `suspended` is true instead of leaving a dead screen.
 */
export function useVoiceResume({ active, isOpen, reconnect }) {
  const [suspended, setSuspended] = useState(false)
  const [resuming,  setResuming]  = useState(false)
  const busyRef = useRef(false)

  // Keep the latest callbacks without re-arming the listeners every render.
  const openRef = useRef(isOpen);      openRef.current = isOpen
  const reconRef = useRef(reconnect);  reconRef.current = reconnect

  const resume = async () => {
    if (busyRef.current) return
    busyRef.current = true
    setResuming(true)
    try {
      await reconRef.current()
      setSuspended(false)
    } catch {
      // Leave `suspended` true: the user gets an explicit tap-to-resume, which
      // also supplies the gesture some browsers require before audio restarts.
      setSuspended(true)
    } finally {
      setResuming(false)
      busyRef.current = false
    }
  }

  const resumeRef = useRef(resume); resumeRef.current = resume

  useEffect(() => {
    if (!active) { setSuspended(false); return }

    const check = () => {
      if (document.visibilityState !== 'visible') return
      // Give the transport a moment to report its real state after wake.
      setTimeout(() => {
        if (!openRef.current() && !busyRef.current) { setSuspended(true); resumeRef.current() }
      }, 400)
    }

    document.addEventListener('visibilitychange', check)
    window.addEventListener('pageshow', check)

    // Backstop: some platforms neither fire visibilitychange nor surface a
    // close event when the socket dies in the background.
    const poll = setInterval(() => {
      if (document.visibilityState === 'visible' && !openRef.current() && !busyRef.current) setSuspended(true)
    }, 4000)

    return () => {
      document.removeEventListener('visibilitychange', check)
      window.removeEventListener('pageshow', check)
      clearInterval(poll)
    }
  }, [active])

  return { suspended, resuming, resume }
}
